import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';

import { datadogRum } from '@datadog/browser-rum';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    Slide,
    Typography,
} from '@mui/material';

import { ObjectTypes, useAnalytics } from 'src/AnalyticsContext';
import { useNativeAuthAvailability } from 'src/auth/SageAuthContext';
import { useSageAuth } from 'src/auth/useSageAuth';
import ConfirmLoginCode from 'src/components/auth/ConfirmLoginCode';
import PhoneNumberLogin from 'src/components/auth/PhoneNumberLogin';
import SSODirectLogin from 'src/components/auth/SSODirectLogin';
import SSOLogin from 'src/components/auth/SSOLogin';
import { snackActions } from 'src/utils/snackBarTools';
import { getSsoDirectLoginDomain, setSsoDirectLoginDomain } from 'src/utils/ssoUtils';

export enum AuthState {
    PhoneNumberLogin = 'phone-number-login',
    ConfirmCode = 'confirm-code',
    SSOLogin = 'sso-login',
    SSODirectLogin = 'sso-direct-login',
}

export enum LogoutDialogTypes {
    LoginRequired = 'login-required',
    SessionExpired = 'session-expired',
    TokenError = 'token-error',
    VerificationRequired = 'verification-required',
}

const LogoutDialogContent: Record<LogoutDialogTypes, { title: string; message: string }> = {
    [LogoutDialogTypes.LoginRequired]: {
        title: 'Login Required',
        message: 'Please log in to use the app.',
    },
    [LogoutDialogTypes.SessionExpired]: {
        title: 'Session Timed Out',
        message: 'Your session has timed out, please log in again to continue using the app.',
    },
    [LogoutDialogTypes.TokenError]: {
        title: "You've been logged out",
        message: 'Please log in again to continue using the app.',
    },
    [LogoutDialogTypes.VerificationRequired]: {
        title: 'Additional Verification Required',
        message:
            "For your security, we couldn't verify this sign-in with a text message code. Please log in using your email and password instead.",
    },
};

// The reason comes from the URL, so only open the dialog for one we have copy for
const isLogoutDialogType = (value?: string | null): value is LogoutDialogTypes =>
    Object.values<string>(LogoutDialogTypes).includes(value ?? '');

function LoginPage() {
    const { login } = useSageAuth();
    const { trackEvent } = useAnalytics();
    const { refresh: refreshNativeAuthAvailability } = useNativeAuthAvailability();

    const [searchParams, setSearchParams] = useSearchParams();

    const [ssoDirectLoginDomain] = useState(getSsoDirectLoginDomain);
    const [authState, setAuthState] = useState(
        ssoDirectLoginDomain ? AuthState.SSODirectLogin : AuthState.PhoneNumberLogin
    );
    const [logoutDialogType, setLogoutDialogType] = useState<LogoutDialogTypes | undefined>(
        undefined
    );
    const [phoneNumber, setPhoneNumber] = useState<string | undefined>(undefined);
    const [isLoggingIn, setIsLoggingIn] = useState(false);
    const [showLogoutDialog, setShowLogoutDialog] = useState(false);

    const handleOnNext = (phoneNumber: string) => {
        setPhoneNumber(phoneNumber);
        setAuthState(AuthState.ConfirmCode);
    };

    const handleOnSSOLogin = () => {
        setAuthState(AuthState.SSOLogin);
    };

    const handleOnBack = () => {
        setAuthState(AuthState.PhoneNumberLogin);
    };

    const handleOnRedirectToPhoneNumberLogin = () => {
        setSsoDirectLoginDomain(undefined);
        setAuthState(AuthState.PhoneNumberLogin);
    };

    const handleConfirmLogout = () => {
        trackEvent('Logout Acknowledged', { objectType: ObjectTypes.Button, logoutDialogType });
        setShowLogoutDialog(false);
        setSearchParams({});
    };

    const handleLogInUsingEmail = async () => {
        trackEvent('Log In Using Email Clicked', { logoutDialogType });
        setIsLoggingIn(true);
        try {
            await login({ forceLogin: true });
        } catch (loginError) {
            datadogRum.addError(loginError, { context: 'emailLogin' });
            snackActions.error('Unable to start email login. Please try again.');
            setIsLoggingIn(false);
        }
    };

    const logoutDialogContent = logoutDialogType
        ? LogoutDialogContent[logoutDialogType]
        : undefined;
    const isVerificationRequired = logoutDialogType === LogoutDialogTypes.VerificationRequired;

    useEffect(() => {
        const logoutReason = searchParams?.get('logoutReason');
        if (isLogoutDialogType(logoutReason)) {
            setShowLogoutDialog(true);
            setLogoutDialogType(logoutReason);
        }
    }, [searchParams]);

    useEffect(() => {
        // make sure we are scrolled to the top with mobile Safari's collapsing URL bar
        window.scrollTo(0, 0);

        if (
            authState !== AuthState.PhoneNumberLogin &&
            authState !== AuthState.ConfirmCode &&
            authState !== AuthState.SSOLogin &&
            authState !== AuthState.SSODirectLogin
        ) {
            // Reset to phone number entry if we are in an invalid state
            setAuthState(AuthState.PhoneNumberLogin);
        }
    }, [authState]);

    useEffect(() => {
        refreshNativeAuthAvailability();
    }, [refreshNativeAuthAvailability]);

    return (
        <>
            <Box
                sx={{
                    position: 'relative',
                    minHeight: '100dvh',
                    overflow: 'hidden',
                    backgroundColor: 'neutral.50',
                }}
            >
                <Slide
                    in={authState === AuthState.SSODirectLogin}
                    direction="left"
                    appear={false}
                    mountOnEnter
                    unmountOnExit
                >
                    <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%' }}>
                        {ssoDirectLoginDomain && (
                            <SSODirectLogin
                                domain={ssoDirectLoginDomain}
                                onRedirectToPhoneNumberLogin={handleOnRedirectToPhoneNumberLogin}
                            />
                        )}
                    </Box>
                </Slide>
                <Slide
                    in={authState === AuthState.PhoneNumberLogin}
                    direction="right"
                    appear={false}
                    mountOnEnter
                    unmountOnExit
                >
                    <PhoneNumberLogin onNext={handleOnNext} onSSOLogin={handleOnSSOLogin} />
                </Slide>
                <Slide
                    in={authState === AuthState.ConfirmCode}
                    direction="left"
                    mountOnEnter
                    unmountOnExit
                >
                    <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%' }}>
                        {phoneNumber && (
                            <ConfirmLoginCode onBack={handleOnBack} phoneNumber={phoneNumber} />
                        )}
                    </Box>
                </Slide>
                <Slide
                    in={authState === AuthState.SSOLogin}
                    direction="left"
                    mountOnEnter
                    unmountOnExit
                >
                    <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%' }}>
                        <SSOLogin onBack={handleOnBack} />
                    </Box>
                </Slide>
            </Box>

            <Dialog
                open={showLogoutDialog}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                fullWidth
                slotProps={{
                    paper: { sx: { borderRadius: 4 } },
                    transition: {
                        onExited: () => {
                            setLogoutDialogType(undefined);
                        },
                    },
                }}
            >
                <DialogContent sx={{ padding: 2.5 }}>
                    <Typography variant="h4" align="center" sx={{ mb: 1.5 }}>
                        {logoutDialogContent?.title}
                    </Typography>
                    <Typography variant="subtitle1" align="center" sx={{ color: 'grey.500' }}>
                        {logoutDialogContent?.message}
                    </Typography>
                </DialogContent>
                <DialogActions
                    disableSpacing
                    sx={{ flexDirection: 'column', gap: 1, padding: 2.5 }}
                >
                    {isVerificationRequired && (
                        <Button
                            variant="contained"
                            onClick={handleLogInUsingEmail}
                            loading={isLoggingIn}
                            disableElevation
                            color="primary"
                            fullWidth
                            size="large"
                        >
                            Log In Using Email
                        </Button>
                    )}
                    <Button
                        variant={isVerificationRequired ? 'text' : 'contained'}
                        onClick={handleConfirmLogout}
                        disableElevation
                        color="primary"
                        fullWidth
                        size="large"
                    >
                        {isVerificationRequired ? 'Not Now' : 'Ok'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default LoginPage;
